import { Container } from '@amelio/ui-library';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';

import { getManagerAssessmentStepPath } from '../Evaluation/stepRoutes';
import { isManagerAssessmentSurveySubmitted } from '../Evaluation/submissionState';
import { HandoutDocument } from '../shared/HandoutDocument/HandoutDocument';
import { PrepareMeetingDownloadSection } from '../shared/PrepareMeetingDownloadSection/PrepareMeetingDownloadSection';
import { ChevronLeftIcon, ChevronRightIcon } from '../shared/PrepareMeetingIcons';
import { PrepareMeetingIntroCard } from '../shared/PrepareMeetingIntroCard/PrepareMeetingIntroCard';
import { PrepareMeetingReadySection } from '../shared/PrepareMeetingReadySection/PrepareMeetingReadySection';
import { StepHeader } from '../shared/StepHeader/StepHeader';
import { buildCompetencyGroups } from '../utils/competencyGroups';
import { getResolvedManagerName, mergeHandoutAnnotations } from '../utils/handoutAnnotations';
import {
    HANDOUT_CAPTURE_EVENT,
    handleDownloadPDF,
    mountHandoutCaptureFrame,
    paginateGroups,
    type HandoutCaptureRequest,
} from '../utils/prepare-meeting';
import { sandwichSortCompetencyGroups } from '../utils/sandwichSort';

import type {
    EvaluationLocationState,
    EvaluationPeriodOption,
    StepDefinition,
} from '../Evaluation/types';
import type { ProficiencyLevel } from '@/types/competencies/ProficiencyLevel';

import { useGetProficiencyLevels } from '@/api/competency-services';
import { useGetCycleInstance } from '@/api/cycle-evaluation-services/cycle-instance';
import {
    useAdvanceManagerAssessmentStep,
    useGetEmployeeCompetencies,
    useGetEvaluationPeriods,
    useSaveEvaluationPeriod,
    useUpdatePerformanceScore,
} from '@/api/cycle-evaluation-services/manager-assessment';
import { useGetUserById } from '@/api/directory';
import { useGetSkillsetAssessment } from '@/api/performance-profile';
import {
    REVIEW_DISCUSSION_QUERY_KEYS,
    useGetHandoutPreview,
    useGetReviewDiscussion,
    useUpdateReviewDiscussion,
} from '@/api/review-services/review-discussion';
import { useRecommendationsGeneration } from '@/components/AiRecommendationPromptModal/useRecommendationsGeneration';
import { HandoutArrowIllustration } from '@/components/Illustrations/Illustration';
import { Bone, Skeleton } from '@/components/Skeleton/Skeleton';
import { useSelectedAppLanguage } from '@/hooks';
import { useFetchErrorsHandler } from '@/hooks/api/useFetchErrorsHandler';
import { useAssessmentReadOnly } from '@/hooks/useAssessmentReadOnly';
import { useError } from '@/hooks/utils/useError';
import { useNavigate } from '@/hooks/utils/useNavigate';
import { showSuccessToast } from '@/plugins/Toast';
import {
    defaultHandoutPreviewData,
    type HandoutPredefinedSectionData,
    type HandoutPreviewData,
} from '@/types/review/ReviewDiscussion';
import { formatPeriodRange } from '@/utils/periodRange';
import { orderProficiencyLevels } from '@/utils/proficiencyLevel';
import { getProfilePictureUrl } from '@/utils/user';

const HANDOUT_CAPTURE_WIDTH_PX = 1024;

const HandoutCaptureFrame = ({ children }: { children: ReactNode }) => {
    const [mountPoint, setMountPoint] = useState<HTMLElement | null>(null);
    const mountFrame = useCallback((frame: HTMLIFrameElement | null) => {
        setMountPoint(frame ? mountHandoutCaptureFrame(frame) : null);
    }, []);

    return (
        <>
            <iframe
                ref={mountFrame}
                className={'pointer-events-none fixed top-0 right-full h-screen border-0'}
                style={{ width: HANDOUT_CAPTURE_WIDTH_PX }}
                aria-hidden={'true'}
                tabIndex={-1}
                inert
                data-handout-capture={'true'}
                data-testid={'handout-capture-frame'}
            />
            {mountPoint && createPortal(children, mountPoint)}
        </>
    );
};

const findDeepActiveElement = (root: DocumentOrShadowRoot): Element | null => {
    const active = root.activeElement;
    return active?.shadowRoot?.activeElement ? findDeepActiveElement(active.shadowRoot) : active;
};

const hasLostFocus = () => {
    const active = findDeepActiveElement(document);
    return active === null || active === document.body;
};

export const ManagerAssessmentPrepareMeetingPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const lang = useSelectedAppLanguage();
    const location = useLocation<EvaluationLocationState | undefined>();
    const queryClient = useQueryClient();

    const state = location.state;
    const { cycleInstanceId: cycleInstanceIdParam } = useParams<{ cycleInstanceId?: string }>();
    const cycleInstanceId = cycleInstanceIdParam ?? state?.cycleInstanceId;

    const { data: cycleInstance } = useGetCycleInstance(cycleInstanceId ?? '');
    const { isReadOnly } = useAssessmentReadOnly(cycleInstance);

    const employeeId =
        state?.employeeId ||
        cycleInstance?.reviewAudience?.reviewAudienceAssignments?.[0]?.assignedTo;
    const { data: employeeUser } = useGetUserById({ userId: employeeId ?? '' });
    const { data: employeeCompetencies = [] } = useGetEmployeeCompetencies(employeeId);
    const { data: skillsetAssessment } = useGetSkillsetAssessment(employeeId);
    const { data: proficiencyLevels = [] } = useGetProficiencyLevels();
    const { data: evaluationPeriodsRaw = [] } = useGetEvaluationPeriods();

    const rawReviewInstanceId =
        cycleInstance?.reviewInstances?.[0]?.id ??
        cycleInstance?.reviewAnswers?.[0]?.reviewInstanceId;
    const reviewInstanceId =
        rawReviewInstanceId && rawReviewInstanceId !== cycleInstanceId
            ? rawReviewInstanceId
            : undefined;

    const [totalPages, setTotalPages] = useState<number>(1);
    const [currentPage, setCurrentPage] = useState(1);
    const [isDownloadLoading, setIsDownloadLoading] = useState(false);
    const [captureRequest, setCaptureRequest] = useState<HandoutCaptureRequest>(null);
    const downloadTriggerRef = useRef<HTMLElement | null>(null);

    const {
        data: handoutData,
        isLoading,
        isError: isHandoutError,
    } = useGetHandoutPreview(reviewInstanceId || '');
    const { data: reviewDiscussion } = useGetReviewDiscussion(reviewInstanceId || '');
    const { mutate: saveDiscussion, isPending: isSavingRecommendations } =
        useUpdateReviewDiscussion(reviewInstanceId || '');
    const { mutate: updatePerformanceScore } = useUpdatePerformanceScore(
        reviewInstanceId || '',
        cycleInstanceId || '',
    );
    const { generate: generateRecommendations } = useRecommendationsGeneration(cycleInstanceId);
    const { mutateAsync: saveEvaluationPeriod } = useSaveEvaluationPeriod();
    const { handleError } = useError();
    const { mutate: advanceManagerAssessmentStep } = useAdvanceManagerAssessmentStep();

    useFetchErrorsHandler({
        errors: reviewInstanceId ? [isHandoutError] : [],
    });

    const hasSubmittedSurvey = isManagerAssessmentSurveySubmitted(cycleInstance);

    // Reaching this page with a submitted survey IS the step-3 (Prepare Meeting) event — nothing else
    // persists it (answer submission stops at step 2, meeting completion jumps to step 4). Persist it
    // once per cycle instance so the dashboard shows 50% and reopens the review here instead of on
    // Results. The backend call is forward-only and idempotent; the ref guards against StrictMode
    // double-mount.
    const advancedForCycleInstanceRef = useRef<string | null>(null);
    useEffect(() => {
        if (!cycleInstanceId || advancedForCycleInstanceRef.current === cycleInstanceId) {
            return;
        }
        if (!hasSubmittedSurvey) {
            return;
        }
        advancedForCycleInstanceRef.current = cycleInstanceId;
        advanceManagerAssessmentStep({ cycleInstanceId, step: 3 });
    }, [cycleInstanceId, hasSubmittedSurvey, advanceManagerAssessmentStep]);

    useEffect(() => {
        const handleCaptureRequest = (event: Event) => {
            setCaptureRequest((event as CustomEvent<HandoutCaptureRequest>).detail);
        };
        window.addEventListener(HANDOUT_CAPTURE_EVENT, handleCaptureRequest);
        return () => window.removeEventListener(HANDOUT_CAPTURE_EVENT, handleCaptureRequest);
    }, []);

    useEffect(() => {
        if (isDownloadLoading || !downloadTriggerRef.current) {
            return;
        }
        if (hasLostFocus()) {
            downloadTriggerRef.current.focus({ preventScroll: true });
        }
        downloadTriggerRef.current = null;
    }, [isDownloadLoading]);

    const orderedProficiencyLevels = useMemo(
        () => orderProficiencyLevels(proficiencyLevels),
        [proficiencyLevels],
    );

    const evaluationPeriods = useMemo<EvaluationPeriodOption[]>(() => {
        return evaluationPeriodsRaw.map((period) => ({
            id: period.id,
            value: period.id,
            label: period.name,
            description: formatPeriodRange({
                from: period.startDate,
                to: period.endDate,
                language: lang,
            }),
            startDate: period.startDate,
            endDate: period.endDate,
            from: period.startDate,
            to: period.endDate,
        }));
    }, [evaluationPeriodsRaw, lang]);

    const derivedCompetencyGroups = useMemo(
        () =>
            buildCompetencyGroups({
                ratedCompetencies: cycleInstance?.assessmentCompetencies ?? [],
                employeeCompetencies,
                skillCategories: skillsetAssessment?.categories ?? [],
                orderedLevels: orderedProficiencyLevels,
            }),
        [
            cycleInstance?.assessmentCompetencies,
            employeeCompetencies,
            skillsetAssessment,
            orderedProficiencyLevels,
        ],
    );

    const sortedCompetencyGroups = useMemo(
        () => sandwichSortCompetencyGroups(derivedCompetencyGroups),
        [derivedCompetencyGroups],
    );

    const paginationResult = useMemo(
        () => paginateGroups(sortedCompetencyGroups, currentPage),
        [sortedCompetencyGroups, currentPage],
    );

    useEffect(() => {
        setTotalPages(paginationResult.totalPages);
    }, [paginationResult.totalPages]);

    const derivedProficiencyLevels = useMemo<ProficiencyLevel[]>(() => {
        if (orderedProficiencyLevels.length === 0) {
            return [];
        }

        return orderedProficiencyLevels.map((level, index) => ({
            id: level.id ?? `${index + 1}`,
            label: level.label,
            sortOrder: level.sortOrder ?? index + 1,
            icon: level.icon ?? '',
            iconColor: level.iconColor ?? null,
        }));
    }, [orderedProficiencyLevels]);

    const handleStepClick = useCallback(
        (step: StepDefinition) => {
            navigate({
                path: getManagerAssessmentStepPath(step.id, cycleInstanceId),
                state,
            });
        },
        [navigate, state, cycleInstanceId],
    );

    const onDownloadPDF = useCallback(async () => {
        const focusedElement = findDeepActiveElement(document);
        downloadTriggerRef.current = focusedElement instanceof HTMLElement ? focusedElement : null;
        setIsDownloadLoading(true);
        try {
            await handleDownloadPDF(reviewInstanceId);
            showSuccessToast({ message: t('postReviewPrepareMeeting.download.success') });
        } catch (error) {
            handleError({
                error,
                data: {
                    title: t('errors.genericTitle'),
                    message: t('postReviewPrepareMeeting.download.error'),
                },
            });
        } finally {
            setIsDownloadLoading(false);
        }
    }, [reviewInstanceId, t, handleError]);

    const handleEvaluationDateSave = (date: string) => {
        if (!cycleInstanceId) return;
        saveEvaluationPeriod({
            cycleInstanceId,
            payload: { evaluationDate: date },
        }).catch((error) => handleError({ error }));
    };

    const handleEvaluationPeriodSave = (startDate: string, endDate: string, periodId: string) => {
        if (!cycleInstanceId) return;
        saveEvaluationPeriod(
            {
                cycleInstanceId,
                payload: {
                    evaluationPeriodStart: startDate,
                    evaluationPeriodEnd: endDate,
                    evaluationPeriodId: periodId || null,
                },
            },
            {
                onSuccess: () => {
                    // Invalidate cycleInstance and handoutPreview queries so both pages refetch
                    queryClient.invalidateQueries({
                        queryKey: ['cycleInstance', cycleInstanceId],
                    });
                    if (reviewInstanceId) {
                        void queryClient.refetchQueries({
                            queryKey: REVIEW_DISCUSSION_QUERY_KEYS.handoutPreview(reviewInstanceId),
                        });
                    }
                },
            },
        ).catch((error) => handleError({ error }));
    };

    const handleHandoutPerformanceScoreSave = (score: string) => {
        if (!reviewInstanceId) return;
        if (score) {
            updatePerformanceScore({
                reviewInstanceId,
                overallPerformanceLevelId: score,
            });
        }
    };

    const handleManagerNameSave = (name: string) => {
        if (!reviewInstanceId) return;
        saveDiscussion({
            handoutAnnotations: mergeHandoutAnnotations(reviewDiscussion?.handoutAnnotations, {
                managerName: name,
            }),
        });
    };

    const handleMeet = () => {
        navigate({ path: getManagerAssessmentStepPath(4, cycleInstanceId), state });
    };

    const handlePreviousPage = () => {
        setCurrentPage((page) => Math.max(1, page - 1));
    };

    const handleNextPage = () => {
        setCurrentPage((page) => Math.min(totalPages, page + 1));
    };

    const handleSavePredefinedSection = (sections: HandoutPredefinedSectionData[]) => {
        if (!reviewInstanceId) {
            return;
        }

        saveDiscussion({ predefinedSection: JSON.stringify(sections) });
    };

    const displayData = useMemo(() => {
        if (!handoutData) {
            return { ...defaultHandoutPreviewData } as HandoutPreviewData;
        }

        const managerName = getResolvedManagerName(
            cycleInstance ?? undefined,
            reviewDiscussion,
            handoutData,
        );

        return {
            ...handoutData,
            managerName,
            employeeAvatarUrl: getProfilePictureUrl(employeeUser?.profilePictureUrl) || undefined,
            competencyGroups: handoutData.competencyGroups ?? derivedCompetencyGroups,
            proficiencyLevels:
                handoutData.proficiencyLevels?.length > 0
                    ? handoutData.proficiencyLevels
                    : derivedProficiencyLevels,
        };
    }, [
        cycleInstance,
        handoutData,
        reviewDiscussion,
        derivedCompetencyGroups,
        derivedProficiencyLevels,
        employeeUser?.profilePictureUrl,
    ]);

    const shouldShowSkeleton = Boolean(reviewInstanceId && isLoading && !handoutData);

    const handoutDocumentProps = {
        handoutData: displayData,
        cycleInstanceId: cycleInstanceId ?? '',
        totalPages,
        onEvaluationDateSave: handleEvaluationDateSave,
        onEvaluationPeriodSave: handleEvaluationPeriodSave,
        evaluationPeriods,
        onPerformanceScoreSave: handleHandoutPerformanceScoreSave,
        employeeName: displayData.employeeName,
        employeeId: employeeId ?? '',
        evaluationDate: displayData.evaluationDate,
        onPredefinedSectionSave: handleSavePredefinedSection,
        onRecommendationsGenerate: generateRecommendations,
        isPredefinedSectionSaving: isSavingRecommendations,
        disabled: isReadOnly,
        onManagerNameSave: handleManagerNameSave,
    };

    return (
        <div data-testid={'manager-assessment-prepare-meeting-page'}>
            <StepHeader
                currentStep={3}
                employeeFirstName={employeeUser?.firstName}
                onStepClick={handleStepClick}
            />

            {/* Two mutually exclusive variants rather than a base-plus-`md:` pair: this app emits
                the plain utility after its own responsive variant, so a base class would win past
                the breakpoint (see src/responsive-utilities.css). */}
            <main
                className={
                    'assessment-journey space-y-6 max-md:pt-18 max-md:pb-16 md:pt-28 md:pb-20'
                }
            >
                <div className={'relative space-y-6'}>
                    {/* Overrides the stack's own 24px for this one child: the intro card stands
                        apart from the handout, with far more room before it than between the
                        handout's own blocks. */}
                    <div className={'relative mb-28 w-full max-w-2xl'}>
                        <PrepareMeetingIntroCard />
                    </div>
                    <HandoutArrowIllustration
                        className={
                            'pointer-events-none absolute top-16 left-[480px] z-10 w-[320px] max-lg:hidden'
                        }
                    />

                    {shouldShowSkeleton ? (
                        <Container
                            direction={'col'}
                            className={'mx-auto w-full max-w-[1024px]'}
                            dataTestId={'prepare-meeting-skeleton'}
                        >
                            <Skeleton>
                                <div className={'space-y-6'}>
                                    <Bone className={'h-7.5 w-50'} />
                                    <Bone className={'h-30 w-full'} />
                                    <Bone className={'h-60 w-full'} />
                                </div>
                            </Skeleton>
                        </Container>
                    ) : (
                        <>
                            <div className={'mx-auto w-full max-w-[1024px]'}>
                                <HandoutDocument
                                    {...handoutDocumentProps}
                                    currentPage={currentPage}
                                    paginatedGroups={paginationResult.groups}
                                />
                            </div>
                            <div
                                className={'mt-15 flex items-center justify-center gap-2'}
                                data-pdf-hide={'true'}
                            >
                                <button
                                    className={
                                        'rounded-md border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-500 disabled:opacity-50'
                                    }
                                    type={'button'}
                                    onClick={handlePreviousPage}
                                    disabled={currentPage === 1}
                                >
                                    <ChevronLeftIcon className={'h-5 w-5 text-gray-500'} />
                                </button>
                                {Array.from({ length: totalPages }, (_, index) => {
                                    const page = index + 1;
                                    const isActive = page === currentPage;
                                    return (
                                        <button
                                            key={page}
                                            className={
                                                isActive
                                                    ? 'text-md rounded-md bg-teal-500 px-4 py-2 font-semibold text-white'
                                                    : 'text-md rounded-md border border-gray-200 bg-white px-4 py-2 font-semibold text-gray-500'
                                            }
                                            type={'button'}
                                            onClick={() => setCurrentPage(page)}
                                        >
                                            {t('common.pageWithNumber', { page })}
                                        </button>
                                    );
                                })}
                                <button
                                    className={
                                        'rounded-md border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-500 disabled:opacity-50'
                                    }
                                    type={'button'}
                                    onClick={handleNextPage}
                                    disabled={currentPage === totalPages}
                                >
                                    <ChevronRightIcon className={'h-5 w-5 text-gray-500'} />
                                </button>
                            </div>
                        </>
                    )}
                </div>

                <PrepareMeetingDownloadSection
                    onDownload={onDownloadPDF}
                    isDisabled={isSavingRecommendations}
                    isLoading={isDownloadLoading}
                />

                <PrepareMeetingReadySection
                    onMeet={handleMeet}
                    isDisabled={isSavingRecommendations}
                    employeeFirstName={employeeUser?.firstName ?? ''}
                />
            </main>

            {captureRequest && (
                <HandoutCaptureFrame>
                    <HandoutDocument
                        {...handoutDocumentProps}
                        currentPage={captureRequest.page}
                        paginatedGroups={
                            paginateGroups(sortedCompetencyGroups, captureRequest.page).groups
                        }
                    />
                </HandoutCaptureFrame>
            )}
        </div>
    );
};
